import { AnyAction } from "redux";
import {
  defaultFilterOptions,
  defaultFiltersLoaded,
  defaultFilterPageState,
  defaultItemType,
} from "../../constants/filters";
import { IBookmark } from "../../types/bookmark.types";
import {
  ItemType,
  IUnitsData,
  IUnitsTotalData,
  UnitTab,
} from "../../types/data.types";
import {
  FilterPageState,
  FilterType,
  IFilter,
  IFilterLoaded,
  IFilterOptions,
  UpcDetailData,
} from "../../types/filters.types";
import { IDropdownOption, IFilterOption } from "../../types/dropdown.types";
import { BarcodeType } from "../../types/barcode.types";
import { ToastMessage } from "../../types/toast.types";
import { LocationCoords } from "../../types/permission.types";

export const transformFilterOptions = (
  filterOptions: IFilterOptions,
): IFilter => ({
  warehouseDivision: filterOptions.warehouseDivision
    .map((option) => option.value)
    .join(","),
  warehouse: filterOptions.warehouse.map((option) => option.value).join(","),
  distribution: filterOptions.distribution
    .map((option) => option.value)
    .join(","),
  dsdVendor: filterOptions.dsdVendor.map((option) => option.value).join(","),
  retailDivision: filterOptions.retailDivision
    .map((option) => option.value)
    .join(","),
  retailStore: filterOptions.retailStore
    .map((option) => option.value)
    .join(","),
  smic: filterOptions.smic.map((option) => option.value).join(","),
  upc: filterOptions.upc.map((option) => option.value).join(","),
  cic: filterOptions.cic.length > 0 ? `${filterOptions.cic[0].value}` : "",
  department: filterOptions.department.map((option) => option.value).join(","),
  retailSection: filterOptions.retailSection
    .map((option) => option.value)
    .join(","),
});

export const transformFilters = (
  type: FilterType,
  filters: IFilterOption[],
) => {
  if (type === "cic" || type === "upc") {
    return filters.map((option: IFilterOption) => ({
      label: option.label,
      value: option.value,
      description: option.description
        ? `${option.label} - ${option.description.split("- ")[1]}`
        : option.label,
    }));
  }
  return filters;
};

export const INIT_DASHBOARD_STATE = "INIT_DASHBOARD_STATE";
export const ON_CHANGE_FILTERS = "ON_CHANGE_FILTERS";
export const UPDATE_PRODUCT_DESCRIPTION = "UPDATE_PRODUCT_DESCRIPTION";
export const GET_CIC_AND_UPC_DETAILS = "GET_CIC_AND_UPC_DETAILS";
export const GET_CIC_AND_UPC_DETAILS_PARTIAL_SUCCESS =
  "GET_CIC_AND_UPC_DETAILS_PARTIAL_SUCCESS";
export const GET_CIC_AND_UPC_DETAILS_SUCCESS =
  "GET_CIC_AND_UPC_DETAILS_SUCCESS";
export const GET_CIC_AND_UPC_DETAILS_ERROR = "GET_CIC_AND_UPC_DETAILS_ERROR";
export const GET_FILTER_OPTIONS = "GET_FILTER_OPTIONS";
export const GET_FILTER_OPTIONS_SUCCESS = "GET_FILTER_OPTIONS_SUCCESS";
export const GET_MULTIPLE_FILTER_OPTIONS_SUCCESS =
  "GET_MULTIPLE_FILTER_OPTIONS_SUCCESS";
export const GET_FILTER_OPTIONS_ERROR = "GET_FILTER_OPTIONS_ERROR";
export const GET_DEFAULT_FILTERS = "GET_DEFAULT_FILTERS";
export const GET_DEFAULT_FILTERS_SUCCESS = "GET_DEFAULT_FILTERS_SUCCESS";
export const GET_DEFAULT_FILTERS_ERROR = "GET_DEFAULT_FILTERS_ERROR";
export const GET_UNITS_DATA = "GET_UNITS_DATA";
export const GET_UNITS_DATA_SUCCESS = "GET_UNITS_DATA_SUCCESS";
export const GET_UNITS_DATA_ERROR = "GET_UNITS_DATA_ERROR";
export const SET_ITEM_TYPE_ACTION = "SET_ITEM_TYPE_ACTION";
export const FLOATING_RESET_ACTION = "FLOATING_RESET_ACTION";
export const FILTERS_RESET_ACTION = "FILTERS_RESET_ACTION";
export const UPDATE_FILTER_UPDATE_KEY = "UPDATE_FILTER_UPDATE_KEY";
export const REFRESH_DATA_ACTION = "REFRESH_DATA_ACTION";
export const ADD_BOOKMARK_ACTION = "ADD_BOOKMARK_ACTION";
export const ON_SELECT_BOOKMARK = "ON_SELECT_BOOKMARK";
export const CHANGE_EXPAND_FLOATING_MENU_ACTION =
  "CHANGE_EXPAND_FLOATING_MENU_ACTION";
export const FILTER_ACCORDIAN_OPEN = "FILTER_ACCORDIAN_OPEN";
export const ON_DELETE_BOOKMARK = "ON_DELETE_BOOKMARK";
export const ON_RENAME_BOOKMARK = "ON_RENAME_BOOKMARK";
export const MARK_AS_DEFAULT = "MARK_AS_DEFAULT";
export const SET_HAS_LOCATION_ACCESS = "SET_HAS_LOCATION_ACCESS";
export const TOGGLE_TOAST_MESSAGE = "TOGGLE_TOAST_MESSAGE";
export const SET_SELECTED_UNIT_TAB = "SET_SELECTED_UNIT_TAB";

export interface IDashboardReducer {
  isStateInitialized: boolean;
  bookmarks: IBookmark[];
  filterOptionsLoading: IFilterLoaded;
  selectedFilterOptions: IFilterOptions;
  filterOptions: IFilterOptions;
  productDescription: string;
  isUnitsDataLoading: number;
  sellingUnitsData: IUnitsData;
  shippingUnitsData: IUnitsData;
  vendorUnitsData: IUnitsData;
  totalWarehouseData: IUnitsTotalData;
  totalStoreData: IUnitsTotalData;
  itemType: ItemType;
  floatingResetKey: number;
  filtersResetKey: number;
  filtersUpdateKey: number;
  refreshDataKey: number;
  selectedBookmarkId: number | null;
  selectedBookmarkIdxDefault: number;
  isBookmarkFilled: boolean;
  floatingMenuChange: boolean;
  filterAccordianOpen: boolean;
  isDetectedLocation: boolean;
  defaultLocationFilters: IFilterOptions | null;
  toastMessage: ToastMessage;
  selectedUnitTab: UnitTab;
  filterPageState: FilterPageState;
}

export const filterAccordianOpenAction = (value: boolean) => ({
  type: FILTER_ACCORDIAN_OPEN,
  value,
});

export const setSelectedUnitTab = (value: UnitTab) => ({
  type: SET_SELECTED_UNIT_TAB,
  value,
});

export const onRenameBookmark = (name: string, id: number) => ({
  type: ON_RENAME_BOOKMARK,
  value: {
    name,
    id,
  },
});

export const refreshLatestDataAction = () => ({
  type: REFRESH_DATA_ACTION,
});

export const markAsDefault = (value: number) => ({
  type: MARK_AS_DEFAULT,
  value,
});

export const initDashboardState = () => ({
  type: INIT_DASHBOARD_STATE,
});

export const changeDashboardFilters = (value: IFilterOptions) => ({
  type: ON_CHANGE_FILTERS,
  value,
});

export const fetchProductDescription = (cic: IFilterOption[]) => ({
  type: UPDATE_PRODUCT_DESCRIPTION,
  value: {
    cic,
  },
});

export const fetchCICAndUPCDetails = (
  data: string,
  type: BarcodeType,
  retailStore: IFilterOption[],
) => ({
  type: GET_CIC_AND_UPC_DETAILS,
  value: {
    data,
    type,
    retailStore,
  },
});

export const fetchCICAndUPCDetailsSuccess = (
  warehouseDivisions: IDropdownOption[],
  warehouses: IDropdownOption[],
) => ({
  type: GET_CIC_AND_UPC_DETAILS_SUCCESS,
  value: { warehouseDivisions, warehouses },
});

export const fetchCICAndUPCDetailsPartialSuccess = (
  data: UpcDetailData,
  type: BarcodeType,
) => ({
  type: GET_CIC_AND_UPC_DETAILS_PARTIAL_SUCCESS,
  value: { data, type },
});

export const fetchCICAndUPCDetailsError = (type: BarcodeType) => ({
  type: GET_CIC_AND_UPC_DETAILS_ERROR,
  value: { type },
});

export const addBookmark = (name: string, id = Date.now()) => ({
  type: ADD_BOOKMARK_ACTION,
  value: {
    name,
    id,
  },
});

export const onSelectBookmark = (value: number) => ({
  type: ON_SELECT_BOOKMARK,
  value,
});

export const onDeleteBookmark = (value: number) => ({
  type: ON_DELETE_BOOKMARK,
  value,
});

export const fetchFilterOptions = (
  type: FilterType | FilterType[],
  query: string,
  pageNumber: number,
  filters: IFilterOptions,
) => ({
  type: GET_FILTER_OPTIONS,
  value: {
    type,
    query,
    allFilters: filters,
    pageNumber,
    filters: transformFilterOptions(filters),
  },
});

export const fetchFilterOptionsSuccess = (
  type: FilterType,
  options: IFilterOption[],
  pageNumber: number,
  hasMore: boolean,
) => ({
  type: GET_FILTER_OPTIONS_SUCCESS,
  value: { type, options, pageNumber, hasMore },
});

export const fetchMultipleFilterOptionsSuccess = (
  options: Partial<IFilterOptions>,
  extraFilters: FilterType[],
  pageState: Partial<FilterPageState>,
) => ({
  type: GET_MULTIPLE_FILTER_OPTIONS_SUCCESS,
  value: { options, extraFilters, pageState },
});

export const fetchFilterOptionsError = (type: FilterType | FilterType[]) => ({
  type: GET_FILTER_OPTIONS_ERROR,
  value: { type },
});

export const fetchDefaultFilters = (
  location: LocationCoords,
  extraFilters: FilterType[],
) => ({
  type: GET_DEFAULT_FILTERS,
  value: {
    location,
    extraFilters,
  },
});

export const fetchDefaultFiltersSuccess = (
  options: Partial<IFilterOptions>,
  extraFilters: FilterType[],
) => ({
  type: GET_DEFAULT_FILTERS_SUCCESS,
  value: {
    options,
    extraFilters,
  },
});

export const fetchDefaultFiltersError = (extraFilters: FilterType[]) => ({
  type: GET_DEFAULT_FILTERS_ERROR,
  value: { extraFilters },
});

export const fetchUnitsData = (
  filters: IFilterOptions,
  isRetailOps: boolean = false,
) => ({
  type: GET_UNITS_DATA,
  value: {
    filters,
    isRetailOps,
  },
});

interface AllUnitsData {
  sellingUnits: IUnitsData;
  shippingUnits: IUnitsData;
  vendorUnits: IUnitsData;
  totalWarehouseData: IUnitsTotalData;
  totalStoreData: IUnitsTotalData;
}

export const fetchUnitsDataSuccess = (value: AllUnitsData) => ({
  type: GET_UNITS_DATA_SUCCESS,
  value,
});

export const fetchUnitsDataError = () => ({
  type: GET_UNITS_DATA_ERROR,
});

export const setItemTypeAction = (value: ItemType) => ({
  type: SET_ITEM_TYPE_ACTION,
  value,
});

export const floatingResetAllAction = () => ({
  type: FLOATING_RESET_ACTION,
});

export const filtersResetAllAction = () => ({
  type: FILTERS_RESET_ACTION,
});

export const updateFilterUpdateKey = () => ({
  type: UPDATE_FILTER_UPDATE_KEY,
});

export const changeExpandFloatingMenuAction = (value: boolean) => ({
  type: CHANGE_EXPAND_FLOATING_MENU_ACTION,
  value,
});

export const setIsDetectedLocation = (value: boolean) => ({
  type: SET_HAS_LOCATION_ACCESS,
  value,
});

export const toggleToastMessage = (value: ToastMessage) => ({
  type: TOGGLE_TOAST_MESSAGE,
  value,
});

export const initialState: IDashboardReducer = {
  isStateInitialized: false,
  bookmarks: localStorage.getItem("user_bookmarks")
    ? JSON.parse(localStorage.getItem("user_bookmarks") ?? "[]")
    : [],
  filterOptionsLoading: defaultFiltersLoaded,
  selectedFilterOptions: defaultFilterOptions,
  filterOptions: defaultFilterOptions,
  productDescription: "",
  isUnitsDataLoading: 2,
  sellingUnitsData: {},
  shippingUnitsData: {},
  vendorUnitsData: {},
  totalWarehouseData: {},
  totalStoreData: {},
  itemType: defaultItemType,
  floatingResetKey: 0,
  filtersResetKey: 0,
  filtersUpdateKey: 0,
  refreshDataKey: 0,
  selectedBookmarkId: null,
  selectedBookmarkIdxDefault: -1,
  isBookmarkFilled: false,
  floatingMenuChange: false,
  filterAccordianOpen: false,
  isDetectedLocation: false,
  defaultLocationFilters: null,
  toastMessage: { show: false },
  selectedUnitTab: 0,
  filterPageState: defaultFilterPageState,
};

export const dashboardReducer = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case INIT_DASHBOARD_STATE:
      let savedBookmarkId: number | null = null;
      let bookmarkIdx1 = -1;
      // initial load default page
      const defaultBookmarkId = localStorage.getItem("mark_as_default");
      if (defaultBookmarkId !== null) {
        bookmarkIdx1 = Number(defaultBookmarkId);
      }
      const selectedBookmarkId = localStorage.getItem("selected_bookmark");
      if (selectedBookmarkId !== null) {
        savedBookmarkId = JSON.parse(selectedBookmarkId);
      }
      if ((bookmarkIdx1 !== null && bookmarkIdx1 > -1) || savedBookmarkId) {
        const filters1 =
          bookmarkIdx1 !== null && bookmarkIdx1 > -1
            ? state.bookmarks[bookmarkIdx1].filters
            : state.bookmarks.find(
                (bookmark) => bookmark.id === savedBookmarkId,
              )?.filters;
        return {
          ...state,
          selectedFilterOptions: filters1
            ? (JSON.parse(filters1) as IFilterOptions)
            : defaultFilterOptions,
          selectedBookmarkIdxDefault: bookmarkIdx1,
          selectedBookmarkId:
            bookmarkIdx1 !== null && bookmarkIdx1 > -1
              ? state.bookmarks[bookmarkIdx1].id
              : savedBookmarkId,
          isBookmarkFilled: true,
          isStateInitialized: true,
          filtersUpdateKey: state.filtersUpdateKey + 1,
        };
      }
      return {
        ...state,
        selectedFilterOptions: state.defaultLocationFilters
          ? {
              ...defaultFilterOptions,
              ...state.defaultLocationFilters,
            }
          : defaultFilterOptions,
        selectedBookmarkIdxDefault: -1,
        selectedBookmarkId: null,
        isBookmarkFilled: false,
        isStateInitialized: true,
        isUnitsDataLoading: 2,
        sellingUnitsData: {},
        shippingUnitsData: {},
        vendorUnitsData: {},
        totalWarehouseData: {},
        totalStoreData: {},
        filtersUpdateKey: state.filtersUpdateKey + 1,
      };
    case ON_CHANGE_FILTERS:
      const filters2 = action.value;
      return {
        ...state,
        selectedFilterOptions: filters2,
        selectedBookmarkId: null,
        isBookmarkFilled: false,
      };
    case UPDATE_PRODUCT_DESCRIPTION:
      return {
        ...state,
        productDescription:
          action.value.cic.length > 0 ? action.value.cic[0].description : "",
      };
    case GET_CIC_AND_UPC_DETAILS:
      const cicOptions1 =
        action.value.type === "cic"
          ? [
              {
                label: action.value.data,
                value: action.value.data,
                description: action.value.data,
              },
            ]
          : [];
      return {
        ...state,
        productDescription: "",
        selectedBookmarkId: null,
        isBookmarkFilled: false,
        isUnitsDataLoading: 2,
        sellingUnitsData: {},
        shippingUnitsData: {},
        vendorUnitsData: {},
        totalWarehouseData: {},
        totalStoreData: {},
        filterOptionsLoading: {
          ...state.filterOptionsLoading,
          upc: true,
          cic: action.value.type === "upc",
        },
        filterOptions: {
          ...state.filterOptions,
          upc: [],
          cic: cicOptions1,
        },
      };
    case GET_CIC_AND_UPC_DETAILS_PARTIAL_SUCCESS: {
      const upcOptionsPartial = action.value.data.upcs.map(
        (upc: IDropdownOption) => ({
          label: upc.label,
          value: upc.value,
          description: upc.label,
        }),
      );
      const cicOptionsPartial = [
        {
          label: action.value.data.cic,
          value: action.value.data.cic,
          description: action.value.data.description,
        },
      ];
      return {
        ...state,
        selectedFilterOptions: {
          ...state.selectedFilterOptions,
          upc: upcOptionsPartial,
          cic: cicOptionsPartial,
        },
        itemType: action.value.data.itemType,
        filterOptions: {
          ...state.filterOptions,
          upc: upcOptionsPartial,
          ...(action.value.type === "upc" ? { cic: cicOptionsPartial } : {}),
        },
        filterOptionsLoading: {
          ...state.filterOptionsLoading,
          upc: false,
          ...(action.value.type === "upc" ? { cic: false } : {}),
        },
      };
    }
    case GET_CIC_AND_UPC_DETAILS_SUCCESS:
      return {
        ...state,
        selectedFilterOptions: {
          ...state.selectedFilterOptions,
          warehouseDivision: action.value.data.warehouseDivisions,
          warehouse: action.value.data.warehouses,
        },
        filterOptions: {
          ...state.filterOptions,
          warehouseDivision: action.value.data.warehouseDivisions,
          warehouse: action.value.data.warehouses,
        },
        filtersUpdateKey: state.filtersUpdateKey + 1,
      };
    case GET_CIC_AND_UPC_DETAILS_ERROR:
      return {
        ...state,
        productDescription: "",
        filterOptionsLoading: {
          ...state.filterOptionsLoading,
          upc: false,
          ...(action.value.type === "upc" ? { cic: false } : {}),
        },
        filtersUpdateKey: state.filtersUpdateKey + 1,
      };
    case ON_RENAME_BOOKMARK:
      const newName = action.value.name;
      const bookmarkIdx2 = action.value.id;
      let storedBookmarksJSONDetails = localStorage.getItem("user_bookmarks");
      const storedBookmarksDetails = storedBookmarksJSONDetails
        ? JSON.parse(storedBookmarksJSONDetails)
        : [];
      storedBookmarksDetails[bookmarkIdx2].name = newName;
      localStorage.setItem(
        "user_bookmarks",
        JSON.stringify(storedBookmarksDetails),
      );
      return {
        ...state,
        bookmarks: storedBookmarksDetails,
        isBookmarkFilled: false,
      };

    case ON_DELETE_BOOKMARK:
      const bookmarkIdx3 = action.value;
      const bookmarkId2 = state.bookmarks[bookmarkIdx3].id;
      const updatedBookmarks = state.bookmarks.filter(
        (_, index) => index !== bookmarkIdx3,
      );
      localStorage.setItem("user_bookmarks", JSON.stringify(updatedBookmarks));
      if (state.selectedBookmarkIdxDefault === bookmarkIdx3) {
        localStorage.setItem("mark_as_default", "-1");
      }
      if (state.selectedBookmarkId === bookmarkId2) {
        localStorage.removeItem("selected_bookmark");
      }
      return {
        ...state,
        bookmarks: updatedBookmarks,
        ...(state.selectedBookmarkIdxDefault === bookmarkIdx3
          ? { selectedBookmarkIdxDefault: -1 }
          : {}),
        ...(state.selectedBookmarkId === bookmarkId2
          ? { selectedBookmarkId: null, isBookmarkFilled: false }
          : {}),
      };
    case MARK_AS_DEFAULT:
      const bookmarkIdx4 = action.value;
      localStorage.setItem("mark_as_default", bookmarkIdx4);
      return {
        ...state,
        selectedBookmarkIdxDefault: bookmarkIdx4,
        isBookmarkFilled: bookmarkIdx4 >= 0,
      };
    case ADD_BOOKMARK_ACTION:
      const bookmarkId = action.value.id;
      const bookmarks = [
        ...state.bookmarks,
        {
          id: bookmarkId,
          name: action.value.name,
          filters: JSON.stringify(state.selectedFilterOptions),
        },
      ];
      localStorage.setItem("user_bookmarks", JSON.stringify(bookmarks));
      localStorage.setItem("selected_bookmark", JSON.stringify(bookmarkId));
      return {
        ...state,
        bookmarks,
        selectedBookmarkId: bookmarkId,
        isBookmarkFilled: true,
      };
    case ON_SELECT_BOOKMARK:
      const filters5 = JSON.parse(
        state.bookmarks[action.value].filters,
      ) as IFilterOptions;
      const bookmarkId3 = state.bookmarks[action.value].id;
      localStorage.setItem("selected_bookmark", JSON.stringify(bookmarkId3));
      return {
        ...state,
        selectedFilterOptions: filters5,
        selectedBookmarkId: bookmarkId3,
        isBookmarkFilled: true,
        filtersUpdateKey: state.filtersUpdateKey + 1,
      };
    case GET_FILTER_OPTIONS:
      if (Array.isArray(action.value.type)) {
        const updatedFilterOptionsLoading1 = {
          ...state.filterOptionsLoading,
        };
        action.value.type.forEach((filterType: FilterType) => {
          updatedFilterOptionsLoading1[filterType] = true;
        });
        const updatedFilterOptions1 = { ...state.filterOptions };
        action.value.type.forEach((filterType: FilterType) => {
          updatedFilterOptions1[filterType] = [];
        });
        return {
          ...state,
          filterOptionsLoading: updatedFilterOptionsLoading1,
          filterOptions: updatedFilterOptions1,
        };
      }
      return {
        ...state,
        filterOptionsLoading: {
          ...state.filterOptionsLoading,
          [action.value.type]: true,
        },
        ...(!action.value.query &&
          action.value.pageNumber === 0 && {
            filterOptions: {
              ...state.filterOptions,
              [action.value.type]: [],
            },
          }),
        filterPageState: {
          ...state.filterPageState,
          [action.value.type]: {
            page: action.value.pageNumber,
            hasMore:
              state.filterPageState[action.value.type as FilterType].hasMore,
          },
        },
      };
    case GET_FILTER_OPTIONS_SUCCESS:
      const {
        type: filterType,
        options,
        pageNumber: pg,
        hasMore,
      } = action.value;
      const transformed = transformFilters(filterType, options);
      const updatedOptions =
        pg > 0
          ? [
              ...(state.filterOptions[filterType as FilterType] ?? []),
              ...transformed,
            ]
          : transformed;
      return {
        ...state,
        filterOptionsLoading: {
          ...state.filterOptionsLoading,
          [filterType]: false,
        },
        filterOptions: {
          ...state.filterOptions,
          [filterType]: updatedOptions,
        },
        filterPageState: {
          ...state.filterPageState,
          [filterType]: { page: pg, hasMore },
        },
        filtersUpdateKey: state.filtersUpdateKey + 1,
      };
    case GET_MULTIPLE_FILTER_OPTIONS_SUCCESS:
      const updatedFilterOptionsLoading2 = {
        ...state.filterOptionsLoading,
      };
      action.value.extraFilters.forEach((filterType: FilterType) => {
        updatedFilterOptionsLoading2[filterType] = false;
      });
      const updatedFilterOptions3 = { ...state.filterOptions };
      action.value.extraFilters.forEach((filterType: FilterType) => {
        updatedFilterOptions3[filterType] = transformFilters(
          filterType,
          action.value.options[filterType],
        );
      });
      const updatedFilterPageState2 = { ...state.filterPageState };
      action.value.extraFilters.forEach((filterType: FilterType) => {
        if (action.value.pageState[filterType]) {
          updatedFilterPageState2[filterType] =
            action.value.pageState[filterType];
        }
      });
      return {
        ...state,
        filterOptionsLoading: updatedFilterOptionsLoading2,
        selectedFilterOptions: {
          ...state.selectedFilterOptions,
          ...action.value.options,
        },
        filterOptions: updatedFilterOptions3,
        filterPageState: updatedFilterPageState2,
        filtersUpdateKey: state.filtersUpdateKey + 1,
      };
    case GET_FILTER_OPTIONS_ERROR:
      if (Array.isArray(action.value.type)) {
        const updatedFilterOptionsLoading3 = {
          ...state.filterOptionsLoading,
        };
        action.value.type.forEach((filterType: FilterType) => {
          updatedFilterOptionsLoading3[filterType] = false;
        });
        const updatedFilterOptions2 = { ...state.filterOptions };
        action.value.type.forEach((filterType: FilterType) => {
          updatedFilterOptions2[filterType] = [];
        });
        return {
          ...state,
          filterOptionsLoading: updatedFilterOptionsLoading3,
          filterOptions: updatedFilterOptions2,
          filtersUpdateKey: state.filtersUpdateKey + 1,
        };
      }
      return {
        ...state,
        filterOptionsLoading: {
          ...state.filterOptionsLoading,
          [action.value.type]: false,
        },
        filterOptions: {
          ...state.filterOptions,
          [action.value.type]: [],
        },
        filtersUpdateKey: state.filtersUpdateKey + 1,
      };
    case GET_DEFAULT_FILTERS:
      const updatedFilterOptionsLoading4 = {
        ...state.filterOptionsLoading,
        retailStore: true,
      };
      action.value.extraFilters.forEach((filterType: FilterType) => {
        updatedFilterOptionsLoading4[filterType] = true;
      });
      return {
        ...state,
        filterOptionsLoading: updatedFilterOptionsLoading4,
      };
    case GET_DEFAULT_FILTERS_SUCCESS:
      const updatedFilterOptionsLoading5 = {
        ...state.filterOptionsLoading,
        retailStore: false,
      };
      action.value.extraFilters.forEach((filterType: FilterType) => {
        updatedFilterOptionsLoading5[filterType] = false;
      });
      return {
        ...state,
        selectedFilterOptions: {
          ...defaultFilterOptions,
          ...action.value.options,
        },
        selectedBookmarkId: null,
        isBookmarkFilled: false,
        filtersResetKey: state.filtersResetKey + 1,
        filtersUpdateKey: state.filtersUpdateKey + 1,
        filterOptionsLoading: updatedFilterOptionsLoading5,
        defaultLocationFilters: {
          ...action.value.options,
        },
      };
    case GET_DEFAULT_FILTERS_ERROR:
      const updatedFilterOptionsLoading6 = {
        ...state.filterOptionsLoading,
        retailStore: false,
      };
      action.value.extraFilters.forEach((filterType: FilterType) => {
        updatedFilterOptionsLoading6[filterType] = false;
      });
      return {
        ...state,
        filterOptionsLoading: updatedFilterOptionsLoading6,
        defaultLocationFilters: null,
      };
    case GET_UNITS_DATA:
      return {
        ...state,
        isUnitsDataLoading: 2,
        sellingUnitsData: {},
        shippingUnitsData: {},
        vendorUnitsData: {},
        totalWarehouseData: {},
        totalStoreData: {},
      };
    case GET_UNITS_DATA_SUCCESS:
      return {
        ...state,
        isUnitsDataLoading: state.isUnitsDataLoading - 1,
        sellingUnitsData: {
          ...state.sellingUnitsData,
          ...action.value.sellingUnits,
        },
        shippingUnitsData: {
          ...state.shippingUnitsData,
          ...action.value.shippingUnits,
        },
        vendorUnitsData: {
          ...state.vendorUnitsData,
          ...action.value.vendorUnits,
        },
        totalWarehouseData: {
          ...state.totalWarehouseData,
          ...action.value.totalWarehouseData,
        },
        totalStoreData: {
          ...state.totalStoreData,
          ...action.value.totalStoreData,
        },
      };
    case GET_UNITS_DATA_ERROR:
      return {
        ...state,
        isUnitsDataLoading: state.isUnitsDataLoading - 1,
      };
    case SET_ITEM_TYPE_ACTION:
      return {
        ...state,
        itemType: action.value,
      };
    case FLOATING_RESET_ACTION:
      localStorage.removeItem("selected_bookmark");
      return {
        ...state,
        floatingResetKey: state.floatingResetKey + 1,
        selectedBookmarkId: null,
        isBookmarkFilled: false,
      };
    case FILTERS_RESET_ACTION:
      return {
        ...state,
        isStateInitialized: false,
      };
    case UPDATE_FILTER_UPDATE_KEY:
      return {
        ...state,
        filtersUpdateKey: state.filtersUpdateKey + 1,
      };
    case REFRESH_DATA_ACTION:
      return {
        ...state,
        refreshDataKey: state.refreshDataKey + 1,
      };
    case CHANGE_EXPAND_FLOATING_MENU_ACTION:
      return {
        ...state,
        floatingMenuChange: action.value,
      };
    case FILTER_ACCORDIAN_OPEN:
      return {
        ...state,
        filterAccordianOpen: action.value,
      };
    case SET_HAS_LOCATION_ACCESS:
      return {
        ...state,
        isDetectedLocation: action.value,
      };
    case TOGGLE_TOAST_MESSAGE:
      return {
        ...state,
        toastMessage: action.value,
      };
    case SET_SELECTED_UNIT_TAB:
      return {
        ...state,
        selectedUnitTab: action.value,
      };
    default:
      return state;
  }
};

export default dashboardReducer;
